import React, {Dispatch, FC, SetStateAction, useEffect, useState} from 'react';
import {Form, message} from "antd";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {clearConfirmPlane, confirmPlane, getPlaneApprovalList} from "../../../../store/reducers/fleetReducer/fleetReducer";
import {defineMessages, useIntl} from "react-intl";
import {CommonButtonMessages} from "../../../../intl/commonMessages/CommonButtonMessages";
import {CommonFormMessages} from "../../../../intl/commonMessages/CommonFormMessages";
import {CustomModal} from "../../../../components/UI/AntCustom/CustomModal";
import {CustomFieldInput} from "../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomDatePicker} from "../../../../components/UI/AntCustom/CustomDatePicker";
import {IPlane} from "../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {InitialPage} from "../../../../utils/constans/pagination/pagination";
import dayjs, {Dayjs} from "dayjs";

const PlaneConfirmModalMessages = defineMessages({
  confirmTitle: {
    id: "planeConfirmModal.confirmTitle",
    defaultMessage: "Confirm aircraft registration",
  },
  confirmDescription: {
    id: "planeConfirmModal.confirmDescription",
    defaultMessage: "Are you sure you want to confirm aircraft registration?",
  },
})

interface IPlaneConfirmModal {
  plane: IPlane;
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  isPending: boolean;
}

export const PlaneConfirmModal: FC<IPlaneConfirmModal> = ({plane, isVisible, setIsVisible, isPending}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [validUntil, setValidUntil] = useState<Dayjs | undefined>(
    plane.validUntil ? dayjs(plane.validUntil) : undefined
  );

  const {
    isLoading,
    isSuccess,
    errorMessage,
  } = useAppSelector(state => state.fleet.confirm);

  useEffect(() => {
    if (isVisible && plane.validUntil) {
      setValidUntil(dayjs(plane.validUntil));
      form.setFieldsValue({validUntil: dayjs(plane.validUntil)});
    }
  }, [isVisible, plane.validUntil, form]);

  useEffect(() => {
    if (!isLoading) {
      if (isSuccess) {
        setIsVisible(false);
        // Обновляем список после успешного подтверждения
        dispatch(getPlaneApprovalList({
          isPending: isPending,
          page: InitialPage,
        }));
      }
      if (errorMessage) {
        message.error(errorMessage);
      }
    }

    return () => {
      dispatch(clearConfirmPlane());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, errorMessage, isLoading])

  const handleOk = () => {
    form.validateFields().then(() => {
      const validUntilValue = form.getFieldValue('validUntil');
      if (!validUntilValue) {
        message.error(intl.formatMessage(CommonFormMessages.ruleRequired));
        return;
      }
      dispatch(confirmPlane({
        planeId: plane.id,
        validUntil: validUntilValue.format('YYYY-MM-DD'),
      }));
    });
  };

  return (
    <CustomModal
      open={isVisible}
      title={intl.formatMessage(PlaneConfirmModalMessages.confirmTitle)}
      centered
      okButtonProps={{loading: isLoading, type: "primary"}}
      okText={intl.formatMessage(CommonButtonMessages.confirm)}
      cancelText={intl.formatMessage(CommonButtonMessages.cancel)}
      onCancel={() => setIsVisible(false)}
      onOk={handleOk}
      afterClose={() => {
        dispatch(clearConfirmPlane());
        form.resetFields();
        setValidUntil(undefined);
      }}
      width={500}
    >
      <Form form={form} layout="vertical">
        <p>{intl.formatMessage(PlaneConfirmModalMessages.confirmDescription)}</p>
        <p><strong>{plane.registrationNumber}</strong></p>
        
        <CustomFieldInput
          label={intl.formatMessage(CommonFormMessages.planeValidUntilLabel)}
          name={'validUntil'}
          rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
        >
          <CustomDatePicker
            placeholder={intl.formatMessage(CommonFormMessages.planeValidUntilPlaceholder)}
            style={{width: "100%"}}
            inputReadOnly={true}
            placement={"bottomLeft"}
            getPopupContainer={trigger => trigger.parentNode as HTMLElement}
            disabledDate={(current) => {
              if (!current) return false;
              return current && current < dayjs().startOf('day');
            }}
            value={validUntil}
            onChange={(date) => {
              setValidUntil(date || undefined);
              form.setFieldsValue({validUntil: date});
            }}
          />
        </CustomFieldInput>
      </Form>
    </CustomModal>
  );
};

